import type { Config, Encode, ETR290Counts, Event, ProbeStatus, Snapshot, UpdateStatus } from './api'
import { STATES, type StateInfo } from './generated/states'

export type Tone = 'ok' | 'warn' | 'bad' | 'off'

// The dot's tone, a state's rank and its timeline segment come from one
// table generated from the node's (internal/states): three tables kept here
// by hand disagreed with the node's severities (WEB-31). A state the table
// does not know is off, rank 0, drawn as unknown.
// (events and central carry states as strings: an older node's, or one
// this page predates)
const info = (state: string) => (STATES as Record<string, StateInfo | undefined>)[state] ?? STATES.unknown

export function tone(state: string): Tone {
    return info(state).tone
}

// The status dot's classes; whole class names, so Tailwind finds them.
const toneBg: Record<Tone, string> = { ok: 'bg-ok', warn: 'bg-warn', bad: 'bg-bad', off: 'bg-off' }

// How bad a state is, for the worst of several (a node's bar, an encode with
// its destinations).
export const stateRank: Record<string, number> = Object.fromEntries(Object.entries(STATES).map(([k, v]) => [k, v.rank]))
// the worse of two states
export const worse = (a: string, b: string) => ((stateRank[b] ?? 0) > (stateRank[a] ?? 0) ? b : a)

export function dot(t: Tone, small = false): string {
    return `inline-block flex-none rounded-full ${small ? 'ml-px size-1.5' : 'size-2'} ${toneBg[t]}`
}

// the fleet page as a wallboard (?view=wall): no header, every node
// collapsed, problems only, 24 h, large names; the user's own settings stay
// a tile's place on a 16:9 picture (a multiview's, a wall's), as CSS
export const rectStyle = (r: { x: number; y: number; w: number; h: number }) => ({
    left: `${r.x * 100}%`,
    top: `${r.y * 100}%`,
    width: `${r.w * 100}%`,
    height: `${r.h * 100}%`,
})
// central's watch page (/watch): one source, large (WatchView)
export const watchPage = typeof location !== 'undefined' && location.pathname.replace(/\/+$/, '') === '/watch'
// central's monitor wall (/wall): saved walls of tiles (WallView)
export const wallPage = typeof location !== 'undefined' && location.pathname.replace(/\/+$/, '') === '/wall'
// central's broadcaster directory (DECISIONS 2026-10-06 "broadcaster directory")
export const broadcastersPage =
    typeof location !== 'undefined' && location.pathname.replace(/\/+$/, '') === '/broadcasters'
export const wallboard = typeof location !== 'undefined' && new URLSearchParams(location.search).get('view') === 'wall'

export function dotClass(state: string, small = false): string {
    return dot(tone(state), small)
}

// A timeline segment's background: colour plus a pattern (whole class
// names, so Tailwind finds them; the table spells them out).
export const segClass = (state: string) => info(state).seg

const toneVar: Record<Tone, string> = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', off: 'var(--off)' }
export function stateColor(state: string): string {
    return toneVar[tone(state)]
}

export function ago(iso: string, now = Date.now()): string {
    return dur(Math.max(0, Math.floor((now - Date.parse(iso)) / 1000)), true)
}

// two digits for the second field ("8m 05s", "1h 03m"): a running duration
// keeps its width but when its first field gains a digit, so what follows
// it on a line does not jump every few seconds
const two = (n: number) => String(n).padStart(2, '0')

export function dur(sec: number, days = false): string {
    const s = Math.floor(sec)
    if (s < 60) return `${s}s`
    if (s < 3600) return `${Math.floor(s / 60)}m ${two(s % 60)}s`
    if (!days || s < 86400) return `${Math.floor(s / 3600)}h ${two(Math.floor((s % 3600) / 60))}m`
    return `${Math.floor(s / 86400)}d ${two(Math.floor((s % 86400) / 3600))}h`
}

export function mbps(kbps: number): string {
    return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mb/s` : `${Math.round(kbps)} kb/s`
}

// All clocks are 24 h in the browser's local time zone (Date is local by
// default; hourCycle h23 forces 00–23 whatever the locale's default is).
export function clock(t: Date | number, seconds = false): string {
    return new Date(t).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        ...(seconds ? { second: '2-digit' } : {}),
        hourCycle: 'h23',
    })
}

export function dayLabel(t: Date | number): string {
    return new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function timeShort(iso: string): string {
    return clock(Date.parse(iso), true)
}

export function dateTime(iso: string | number): string {
    const t = typeof iso === 'number' ? iso : Date.parse(iso)
    return `${dayLabel(t)} ${clock(t, true)}`
}

export function stateLabel(s: Snapshot | undefined): string {
    if (!s) return '—'
    return s.state.replace('_', ' ')
}

export function slug(name: string): string {
    return (
        name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 24) || 'x'
    )
}

export function newId(prefix: string, taken: Set<string>): string {
    for (let i = 1; ; i++) {
        const id = `${prefix}${i}`
        if (!taken.has(id)) return id
    }
}

// hoursText is a duration in hours as the cost lines say it: "40 min", "3 h 5 min"
export const hoursText = (h: number) =>
    h < 1 ? `${Math.round(h * 60)} min` : `${Math.floor(h)} h ${Math.round((h % 1) * 60)} min`

// one way to say sizes, everywhere (WEB-48: three, each rounding its own way)
export function bytes(n: number): string {
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`
    return `${Math.round(n / 1e3)} kB`
}

// Tooltip position next to the cursor, kept inside the viewport (tooltips are
// at most 360 px wide; a fixed-position box near the right edge otherwise
// runs off screen).
export function tipPos(e: MouseEvent, w = 380, h = 240): { x: number; y: number } {
    return { x: Math.min(e.clientX + 12, window.innerWidth - w), y: Math.min(e.clientY + 12, window.innerHeight - h) }
}

// Latest state per "kind/entity" from a list of events (oldest first or not).
export function latestStates(events: Event[]): Map<string, Event> {
    const m = new Map<string, Event>()
    for (const e of events) {
        if (e.type !== 'state') continue
        const k = e.kind + '/' + e.entity
        const cur = m.get(k)
        if (!cur || Date.parse(e.ts) >= Date.parse(cur.ts)) m.set(k, e)
    }
    return m
}

// Whether an event belongs in an event list. The machine going back to "ok"
// is everything as it should be, not news: the timeline still draws it (the
// end of a high-load stretch), the lists leave it out.
export const listed = (e: Event) => !(e.type === 'state' && e.state === 'ok')

// An entity of the config now: a removed input, encode, destination, output
// or storage keeps its last state event in the log, a no signal for ever,
// but is no alarm any more (as on central). The machine, and kinds the
// config does not list, always are.
export function inConfig(cfg: Config, kind: string, id: string): boolean {
    const list = {
        input: cfg.inputs,
        encode: cfg.encodes,
        destination: cfg.destinations,
        output: cfg.outputs ?? [],
        storage: cfg.storage ?? [],
    }[kind]
    return !list || list.some((x) => x.id === id)
}
// worth naming where a bar is hovered or clicked: a warning or an error, or
// a state the bar draws as anything but green (starting, connecting,
// disabled …). An info "starting" that lasted a day drew a yellow bar whose
// hover said "no events" (Niels)
export const notable = (e: Event) => e.severity !== 'info' || (e.type === 'state' && tone(e.state ?? '') !== 'ok')

// Listed events of one entity (plus, for an encode, its destinations) inside a window.
export function eventsIn(
    events: Event[],
    kind: string,
    id: string,
    extraIds: Set<string>,
    w: { from: number; to: number },
): Event[] {
    return inWindow(
        events,
        (e) => (e.kind === kind && e.entity === id) || (e.kind === 'destination' && extraIds.has(e.entity)),
        w,
    )
}

// The listed events of the entities `mine` accepts within a window, and
// the problems already going on when it starts: an entity whose last state
// before the window is a warning or an error (a no signal that began an hour
// ago) is part of every stretch of it, so clicking anywhere on the red shows
// what it is.
export function inWindow(events: Event[], mine: (e: Event) => boolean, w: { from: number; to: number }): Event[] {
    const before = new Map<string, Event>()
    const inside: Event[] = []
    for (const e of events) {
        if (!mine(e)) continue
        const t = at(e)
        if (t < w.from && e.type === 'state') before.set(e.kind + '/' + e.entity, e)
        else if (t >= w.from && t <= w.to && listed(e)) inside.push(e)
    }
    const carried = [...before.values()].filter(notable)
    return [...carried, ...inside]
}

// When each state event ended (by id): its entity's next state change, null
// while it lasts. One pass over the events, by entity in time order.
export function stateEnds(events: Event[]): Map<number, number | null> {
    const byEntity = new Map<string, { id: number; t: number }[]>()
    for (const e of events) {
        if (e.type !== 'state') continue
        const k = e.kind + '/' + e.entity
        let l = byEntity.get(k)
        if (!l) byEntity.set(k, (l = []))
        l.push({ id: e.id, t: at(e) })
    }
    const out = new Map<number, number | null>()
    for (const l of byEntity.values()) {
        l.sort((a, b) => a.t - b.t)
        l.forEach((x, i) => out.set(x.id, l[i + 1]?.t ?? null))
    }
    return out
}

// What kind a row is, in front of its name: generic, never a number
// (DECISIONS 2026-10-19 "kind tags"); '' for the machine and storage.
export const kindTag = (kind: string): string =>
    ({ input: 'IN', output: 'OUT', encode: 'ENC', destination: 'DEST' })[kind] ?? ''

// The entity's name when the config knows it, otherwise "input srt-in".
// withEncode prefixes a destination with its encode's name (fleet page,
// where one list mixes every encode's destinations).
export function entityLabel(cfg: Config | null | undefined, kind: string, id: string, withEncode = false): string {
    const fallback = `${kind} ${id}`.trim()
    if (!cfg) return fallback
    if (kind === 'destination') {
        const d = cfg.destinations.find((x) => x.id === id)
        const enc = d && withEncode ? cfg.encodes.find((e) => e.id === d.encode_id) : undefined
        return !d ? fallback : enc ? `${enc.name} › ${d.name}` : d.name
    }
    const items: { id: string; name: string }[] =
        kind === 'input' ? cfg.inputs : kind === 'encode' ? cfg.encodes : kind === 'output' ? (cfg.outputs ?? []) : []
    return items.find((x) => x.id === id)?.name ?? fallback
}

// Ports that listen on this node, for the forms' hints: SRT listener inputs
// and srt_listener destinations (UDP), RTMP listener inputs, rtmp_pull and
// hls_pull destinations (TCP), pull destinations at their effective port. The node
// refuses a clash on save; this lets the forms show it first.
export interface PortUse {
    proto: 'udp' | 'tcp'
    port: number
    what: string
    key: string
}
export function portsInUse(cfg: Config, def?: { rtmp: number; srt: number; hls?: number }): PortUse[] {
    const out: PortUse[] = []
    for (const i of cfg.inputs) {
        if ((i.kind === 'srt_listener' || i.kind === 'caster') && i.port)
            out.push({ proto: 'udp', port: Number(i.port), what: `input ${i.name}`, key: 'input:' + i.id })
        if (i.kind === 'rtmp_listener')
            out.push({ proto: 'tcp', port: urlPort(i.url, 1935), what: `input ${i.name}`, key: 'input:' + i.id })
        if (i.kind === 'rist' && ristListenPort(i.url))
            out.push({ proto: 'udp', port: ristListenPort(i.url), what: `input ${i.name}`, key: 'input:' + i.id })
    }
    for (const d of cfg.destinations) {
        if (d.kind === 'rist' && ristListenPort(d.url))
            out.push({
                proto: 'udp',
                port: ristListenPort(d.url),
                what: `destination ${d.name}`,
                key: 'destination:' + d.id,
            })
        if (d.kind === 'srt_listener')
            out.push({
                proto: 'udp',
                port: Number(d.port) || (def?.srt ?? 8890),
                what: `destination ${d.name}`,
                key: 'destination:' + d.id,
            })
        if (d.kind === 'rtmp_pull' || d.kind === 'hls_pull')
            out.push({
                proto: 'tcp',
                port: Number(d.port) || (d.kind === 'hls_pull' ? (def?.hls ?? 8888) : (def?.rtmp ?? 1935)),
                what: `destination ${d.name}`,
                key: 'destination:' + d.id,
            })
    }
    return out.sort((a, b) => a.port - b.port)
}
// An entity's CPU as operators read it: its share of the whole machine
// ("15%"), from the cores the node reports (Docker and top say 290% for the
// same 2.9 cores). One decimal below 10%.
export const cpuShare = (cores: number, machineCores: number) => (cores / (machineCores || 1)) * 100
// utcOffsetMin is the UTC offset, in minutes, a timestamp is written in
// ("…+02:00" 120, "…Z" 0): the node writes its status time in its own zone,
// so this is the node's; null when it carries none
export function utcOffsetMin(iso: string | undefined): number | null {
    const m = iso?.match(/(?:([+-])(\d\d):?(\d\d)|Z)$/)
    if (!m) return null
    if (!m[1]) return 0
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]))
}
// clockAt is the time of day at ms since the epoch in a zone offsetMin from
// UTC: "21:45:03"
export function clockAt(ms: number, offsetMin: number): string {
    const d = new Date(ms + offsetMin * 60000)
    const two = (n: number) => String(n).padStart(2, '0')
    return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}:${two(d.getUTCSeconds())}`
}
// utcLabel: "UTC", "UTC+2", "UTC-5", "UTC+5:30"
export function utcLabel(offsetMin: number): string {
    if (offsetMin === 0) return 'UTC'
    const a = Math.abs(offsetMin)
    return `UTC${offsetMin < 0 ? '-' : '+'}${Math.floor(a / 60)}${a % 60 ? ':' + String(a % 60).padStart(2, '0') : ''}`
}
// a frame rate for people: "59.94", not "60000/1001"; "" stays ""
export function fpsLabel(fps: string): string {
    const [num, den] = fps.split('/').map(Number)
    if (!den || !num) return fps
    return String(Math.round((num / den) * 100) / 100)
}
export const pct = (v: number) => `${v < 10 ? v.toFixed(1) : Math.round(v)}%`
export function urlPort(url: string | undefined, def: number): number {
    const m = url?.match(/^\w+:\/\/[^/:]*:(\d+)/)
    return m ? Number(m[1]) : def
}
// the UDP port a rist:// URL listens on (rist://@:5000), 0 when it connects out
export function ristListenPort(url: string | undefined): number {
    const m = url?.match(/^rist:\/\/@[^/:?]*:(\d+)/)
    return m ? Number(m[1]) : 0
}
export function freePort(used: PortUse[], proto: string, from: number): number {
    let p = from
    while (used.some((u) => u.proto === proto && u.port === p)) p++
    return p
}

// Tick positions for a window [from, to] in ms: 4-8 ticks at a round step,
// x in [0, 1). Days are labelled by date, and midnight and every 6 h major.
export function ticksFor(from: number, to: number): { x: number; label: string; major: boolean }[] {
    const span = to - from
    const steps = [60e3, 2 * 60e3, 5 * 60e3, 15 * 60e3, 30 * 60e3, 3600e3, 2 * 3600e3, 6 * 3600e3, 12 * 3600e3, 86400e3]
    const step = steps.find((s) => span / s <= 8) ?? 86400e3
    const out: { x: number; label: string; major: boolean }[] = []
    // round in local time: at UTC+2 the 6-hour ticks fell on 02:00, 08:00...
    // ponytail: the offset at the window's start; a DST change inside a 7 d
    // window shifts the later day ticks by an hour
    const off = -new Date(from).getTimezoneOffset() * 60e3
    for (let t = Math.ceil((from + off) / step) * step - off; t < to; t += step) {
        const d = new Date(t)
        const midnight = d.getHours() === 0 && d.getMinutes() === 0
        const label = step >= 86400e3 || (span > 86400e3 && midnight) ? dayLabel(d) : clock(d)
        out.push({ x: (t - from) / span, label, major: midnight || (step >= 3600e3 && d.getHours() % 6 === 0) })
    }
    return out
}

// A timeline bar's segments over [start, end] from an entity's state
// events: it starts in the last state before the window ('unknown' without
// one), x and w are fractions of the window and add up to 1. Past known
// (when the node page last heard from its node, while it is disconnected)
// nothing is known: the last state stretched on to now looked as if all was
// well.
// at is an event's time in ms, parsed once per event: the node page's bars
// parsed every event's time for every bar every second (WEB-33)
const parsed = new WeakMap<Event, number>()
export function at(e: Event): number {
    let t = parsed.get(e)
    if (t === undefined) parsed.set(e, (t = Date.parse(e.ts)))
    return t
}

export function segments(
    evs: Event[],
    start: number,
    end: number,
    known = Infinity,
): { x: number; w: number; state: string }[] {
    const span = end - start
    const last = Math.max(start, Math.min(end, known))
    let state = 'unknown'
    for (const e of evs) if (at(e) < start) state = e.state || state
    const out: { x: number; w: number; state: string }[] = []
    let from = start
    for (const e of evs) {
        const t = at(e)
        if (t < start || t > last) continue
        if (t > from) out.push({ x: (from - start) / span, w: (t - from) / span, state })
        state = e.state || state
        from = t
    }
    if (last > from || last === end) out.push({ x: (from - start) / span, w: (last - from) / span, state })
    if (last < end) out.push({ x: (last - start) / span, w: (end - last) / span, state: 'unknown' })
    return out
}

// The worst state of several entities over time, as synthetic state events
// (the fleet page's node and encode bars). With base, that entity's own
// state is the start and the others only make it worse (a disabled encode
// stays disabled, not green). While the node is offline nothing is known.
export function worstOver(evs: Event[], good: string, base?: string): Event[] {
    const cur = new Map<string, string>()
    const out: Event[] = []
    let last = ''
    for (const e of evs) {
        if (e.type !== 'state' || !e.state) continue
        cur.set(e.kind + '/' + e.entity, e.state)
        let worst = (base && cur.get(base)) || good
        for (const s of cur.values()) if ((stateRank[s] ?? 0) > (stateRank[worst] ?? 0)) worst = s
        if (
            e.kind === 'node'
                ? e.state === 'offline'
                : [...cur].some(([k, s]) => k.startsWith('node/') && s === 'offline')
        )
            worst = 'unknown'
        if (worst !== last) out.push({ ...e, state: worst })
        last = worst
    }
    return out
}

// An entity's events with what nobody knows marked: from the node going
// away (conn: to central its offline and online events, to the node page
// noded's stop and start) until it is back the entity's state is unknown;
// back, its last known state again until its next event. Its other events
// pass through. The two marks are events of their own, each with an id
// below zero, so a list keyed by id and stateEnds tell them apart.
// ponytail: a crash or a power cut writes no stop, so the last state runs
// on to the next start; noded could write the stop at boot, dated when its
// last run last wrote a series sample.
let marks = 0
const goneBy = (e: Event) =>
    e.type === 'stop' ? 'noded stopped' : e.type === 'state' && e.state === 'offline' ? 'node offline' : ''
const back = (e: Event) => e.type === 'start' || (e.type === 'state' && e.state === 'online')
export function withNode(conn: Event[], own: Event[]): Event[] {
    if (!conn.length || !own.length) return own
    const all = [...own, ...conn].sort((a, b) => at(a) - at(b))
    const out: Event[] = []
    let last: Event | undefined
    let off = false
    for (const e of all) {
        if (e.kind !== 'node') {
            if (e.type === 'state') last = e
            out.push(e)
        } else if (goneBy(e) && !off && last) {
            off = true
            const message = goneBy(e) + ': state unknown'
            out.push({ ...last, id: --marks, ts: e.ts, state: 'unknown', severity: 'info', message })
        } else if (back(e) && off) {
            off = false
            if (last) out.push({ ...last, id: --marks, ts: e.ts })
        }
    }
    return out
}

// a size given in GB as the storage lists show it: "740 GB", "3.6 TB"
export const gbText = (gb: number) => (gb >= 1000 ? (gb / 1000).toFixed(1) + ' TB' : gb.toFixed(0) + ' GB')

// the machine's and a storage's "ok" is drawn as a running entity is
export const okAsRunning = (state: string | undefined) => (state === 'ok' ? 'running' : (state ?? ''))

// A secret as the node sends it back: set but never shown (SEC-2; the
// node's config.SecretKept). In an address to copy it becomes a marker to
// replace, never the placeholder itself.
export const SECRET_KEPT = '**********'
export const secretText = (v: string, what: string) => (v === SECRET_KEPT ? `<${what}>` : v)

// The machine's network and time settings, checked as the node checks them
// (netcfg.go, system.go), so Apply never fails on what the form could tell.
const ipv4Re = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/
export const isIPv4 = (s: string) => ipv4Re.test(s)
const ipv4Num = (s: string) => s.split('.').reduce((n, p) => n * 256 + Number(p), 0)
// "10.0.0.5/24": an IPv4 address with a prefix of /8 to /32 ('' when it is one)
export function cidrError(s: string): string {
    const [ip, pre, extra] = s.split('/')
    if (extra !== undefined || !ip || !isIPv4(ip) || pre === undefined || !/^\d{1,2}$/.test(pre) || Number(pre) > 32)
        return 'an IPv4 address with its prefix, like 10.0.0.5/24'
    if (Number(pre) < 8) return 'a prefix shorter than /8 is a typo'
    return ''
}
export function inSubnet(ip: string, cidr: string): boolean {
    const [net, pre] = cidr.split('/')
    if (!net || !isIPv4(ip) || !isIPv4(net)) return false
    const bits = Number(pre)
    const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0
    return (ipv4Num(ip) & mask) >>> 0 === (ipv4Num(net) & mask) >>> 0
}
// an IPv4 or IPv6 address (DNS servers may be either)
// What the node refuses, said under the field before Save (WEB-37: typed
// freely and refused only after it, as one toast). Each mirrors the rule in
// internal/config, with its words; "" when fine.
export function streamNameError(s: string): string {
    return s === '' || /^[A-Za-z0-9._~-]+$/.test(s) ? '' : 'letters, digits and . _ ~ - only (it goes in a URL)'
}
export function ndiNameError(s: string): string {
    return /[()\n]/.test(s) ? "no brackets or line breaks (NDI adds the machine's name in brackets)" : ''
}
export function smbShareError(s: string): string {
    const t = s.replace(/^\/+|\/+$/g, '')
    if (s.split('/').some((p) => p === '.' || p === '..')) return 'no . or .. in the path'
    return t.length <= 200 && /^[A-Za-z0-9._$-]+(\/[A-Za-z0-9._-]+)*$/.test(t)
        ? ''
        : 'its name, optionally /folder (letters, digits, . _ - $, no spaces)'
}
export function nfsExportError(s: string): string {
    if (s.split('/').some((p) => p === '.' || p === '..')) return 'no . or .. in the path'
    return s.length <= 200 && /^(\/[A-Za-z0-9._-]+)+\/?$/.test(s) ? '' : 'an absolute path such as /volume1/recordings'
}
// an AES67 output's address: a multicast group or a receiver, IPv4
export function aes67AddressError(s: string): string {
    return isIPv4(s.trim()) ? '' : "a multicast group (239.69.x.y as Dante uses) or a receiver's IPv4 address"
}
// a name or slate text: one line, no control characters
export function oneLineError(s: string): string {
    // eslint-disable-next-line no-control-regex
    return /[\x00-\x1f]/.test(s) ? 'no control characters or line breaks' : ''
}

export const isIP = (s: string) => isIPv4(s) || (/^[0-9a-fA-F:]+$/.test(s) && s.includes(':') && s.length <= 39)
const hostRe = /^[A-Za-z0-9]([A-Za-z0-9.-]{0,251}[A-Za-z0-9])?$/
export const isHost = (s: string) => isIP(s) || hostRe.test(s)

// an output's kind in a word, as the lists show it
export function outputKind(kind: string): string {
    return (
        ({ decklink: 'SDI', ndi: 'NDI', hdmi: 'HDMI', alsa: 'Sound card', aes67: 'AES67' } as Record<string, string>)[
            kind
        ] ?? kind
    )
}
// an audio output's channels: "ch 3+4", "ch 1, 2, 5"
export function channelsText(chans: number[] | undefined): string {
    const [a, b, ...rest] = chans?.length ? chans : [1, 2]
    return (
        'ch ' +
        (b !== undefined && a !== undefined && !rest.length && b === a + 1 ? `${a}+${b}` : (chans ?? []).join(', '))
    )
}

// An RTMP push URL as its server and stream key (DECISIONS 2026-10-06
// "broadcaster directory", answer 1): the key is the last path element
// (rtmp://a.rtmp.youtube.com/live2/<key>), with any query after it; a URL
// with one path element (rtmp://host/app) has none. Joined again as typed.
export function splitRTMP(url: string): { server: string; key: string } {
    const m = /^(rtmps?:\/\/[^/?#]+\/[^?#]*?)\/([^/]+)$/i.exec(url.trim())
    return m ? { server: m[1]!, key: m[2]! } : { server: url.trim(), key: '' }
}
export function joinRTMP(server: string, key: string): string {
    const s = server.trim().replace(/\/+$/, '')
    const k = key.trim().replace(/^\/+/, '')
    return k ? `${s}/${k}` : s
}
// An encode row's summary of its destinations' trouble (the fleet's; the
// encode's own dot stays its own state, WEB-38): "" when none is failing.
export function destsFailing(states: string[]): string {
    const bad = states.filter((s) => tone(s) === 'bad').length
    return bad ? `${bad} of ${states.length} destination${states.length === 1 ? '' : 's'} failing` : ''
}

// A URL to show: a password in it and query values that are credentials
// (token, key, passphrase …) masked. Inputs that pull from a CDN with
// ?token=, rtmp://user:pass@ or an SRT ?passphrase= were printed in full to
// every user of the node page and its events (WEB-35). Copy buttons copy the
// real one: it is what a sender needs.
const secretParam =
    /([?&;](?:token|key|passphrase|pass|password|pwd|secret|auth|sig|signature|access_token|streamkey|cid)=)[^&;#\s]*/gi
export function maskURL(u: string): string {
    return u.replace(/^([a-z][a-z0-9+.-]*:\/\/[^/@?#\s:]*):[^/@?#\s]*@/i, '$1:•••@').replace(secretParam, '$1•••')
}
// maskURLs masks every URL in a text (an event's message: ffmpeg's error
// lines can carry the URL it was given)
export const maskURLs = (text: string) => text.replace(/[a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+/gi, (u) => maskURL(u))

// the URL to show: the key masked
export function maskRTMP(url: string): string {
    const { server, key } = splitRTMP(url)
    return key ? `${server}/••••••` : server
}

// the size a wall's tile asks its picture at, by its height in device
// pixels: a small tile gets a small, cheaper encode on its node (360p or
// 540p, at half rate above 30 fps); a large one the full view (""). Capped
// by the tile's share of the wall's height, whatever the screen (MON-1: a
// 3x3 wall on a 4K TV asked nine full views of one node): a third or less
// at most 540p, a quarter or less 360p.
export function sizeFor(px: number, share = 1): '' | '@360' | '@540' {
    if (px <= 400 || share <= 0.26) return '@360'
    if (px <= 640 || share <= 0.34) return '@540'
    return ''
}

// freeAES67Group picks a multicast group of Dante's AES67 range
// (239.69.x.y) that none of taken uses: the node's own outputs and every
// stream heard announced on the audio network. A random pick checked only
// against the node's own could land on a Dante device's group, or another
// node's, and a receiver got two streams on one group (AUD-21).
export function freeAES67Group(taken: Iterable<string>, random: () => number = Math.random): string {
    const used = new Set(taken)
    const pick = () => `239.69.${1 + Math.floor(random() * 254)}.${1 + Math.floor(random() * 254)}`
    for (let i = 0; i < 1000; i++) {
        const g = pick()
        if (!used.has(g)) return g
    }
    return pick() // 64,516 groups: only a test's random ends here
}

// TR 101 290's items on an encode's stream (output probes): the errors but
// 2.3a's, which is counted and not warned of (a stream under 25 fps cannot
// keep its PCRs 40 ms apart), and each item with errors, priority 1 first
const etrItems: [keyof ETR290Counts, string][] = [
    ['sync_loss', 'sync loss (1.1)'],
    ['sync_byte', 'sync byte (1.2)'],
    ['pat', 'PAT (1.3)'],
    ['continuity', 'continuity (1.4)'],
    ['pmt', 'PMT (1.5)'],
    ['pid', 'PID missing (1.6)'],
    ['transport', 'transport error (2.1)'],
    ['crc', 'CRC (2.2)'],
    ['pcr_repetition', 'PCR repetition (2.3a)'],
    ['pcr_discontinuity', 'PCR discontinuity (2.3b)'],
]
export function etrErrors(c: ETR290Counts): number {
    return etrItems.reduce((n, [k]) => (k === 'pcr_repetition' ? n : n + c[k]), 0)
}
export function etrDescribe(c: ETR290Counts): string {
    return etrItems
        .filter(([k]) => c[k] > 0)
        .map(([k, what]) => `${c[k]} ${what}`)
        .join(', ')
}

// the node software updater's last run in a word or two: a run installs,
// then watches the new version until it has answered a while with no
// restart, or rolls it back (ese-rollback, up to 2 min); an updater from
// before the phases says only "running"
export function updateStep(st: Pick<UpdateStatus, 'state' | 'phase'>): string {
    if (st.state !== 'running') return st.state ?? ''
    return st.phase === 'checking' ? 'checking the new version…' : 'installing…'
}

// the checks of what an encode sends it opted in to
export type ProbeChecks = Pick<Encode, 'check_black' | 'check_frozen' | 'check_silent'>
export const probed = (e: ProbeChecks) => !!(e.check_black || e.check_frozen || e.check_silent)

// an encode's probe in a few words: what its checks found (10 s of it: a
// warning), or "ok"; "" while it has seen nothing
export function probeText(p: ProbeStatus | undefined, e: ProbeChecks): string {
    if (!p) return ''
    const found: string[] = []
    if (e.check_black && (p.black_s ?? 0) >= 10) found.push(`black ${p.black_s} s`)
    else if (e.check_frozen && (p.frozen_s ?? 0) >= 10) found.push(`frozen ${p.frozen_s} s`)
    p.silent_s.forEach((s, i) => {
        if (e.check_silent && s >= 10) found.push(`track ${i + 1} silent ${s} s`)
    })
    return found.join(', ') || 'ok'
}
